import React, { useEffect, useRef, useState } from 'react';
import { SkeletonBlock } from '../SkeletonBlock/SkeletonBlock.web';
import { Text } from '../Text/Text.web';
import type { ProgressListCardSkeletonProps } from './ProgressListCardSkeleton';
import { progressListCardLayout as layout } from './ProgressListCardLayout.web';
import {
  PROGRESS_LIST_CARD_CALLOUT_TITLE_VARIANT,
  PROGRESS_LIST_CARD_CALLOUT_BODY_VARIANT,
} from './ProgressListCard';

type Props = {
  callout: NonNullable<ProgressListCardSkeletonProps['rows'][number]['callout']>;
  active: boolean;
  testID?: string;
};

/** Private measured silhouette; text reserves geometry without painting nested placeholders. */
export function ProgressListCardSkeletonCallout({ callout, active, testID }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const measure = () => setHeight(node.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [callout.title, callout.body, callout.bodyLines]);
  return (
    <div ref={ref} data-testid={testID} aria-hidden="true" style={{ position: 'relative' }}>
      <div style={{ ...layout.callout, visibility: 'hidden', whiteSpace: 'pre-wrap' }}>
        <div style={layout.calloutText}>
          {callout.title ? (
            <Text value={callout.title} variant={PROGRESS_LIST_CARD_CALLOUT_TITLE_VARIANT} />
          ) : null}
          <Text
            value={
              callout.body || Array.from({ length: callout.bodyLines }, () => '\u2007').join('\n')
            }
            variant={PROGRESS_LIST_CARD_CALLOUT_BODY_VARIANT}
            data-testid={testID ? `${testID}-geometry` : undefined}
          />
        </div>
      </div>
      {height > 0 ? (
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <SkeletonBlock
            width="fill"
            height={height}
            radius="xl"
            active={active}
            data-testid={testID ? `${testID}-block` : undefined}
          />
        </div>
      ) : null}
    </div>
  );
}
